import { useEffect } from 'react';
// Import the full no-external bundle to avoid CSP issues and ensure all dependencies are pre-bundled
import posthog from 'posthog-js/dist/module.full.no-external';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from './CookieConsentProvider';

interface PostHogProviderProps {
  children: React.ReactNode;
}

const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const location = useLocation();
  const cookieConsent = useCookieConsent();

  useEffect(() => {
    // Get PostHog configuration from environment variables
    const posthogApiKey = import.meta.env.VITE_POSTHOG_API_KEY;
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
    
    // Only initialize PostHog if we're not in development, have required config, and user consented
    if (
      posthogApiKey && 
      posthogHost && 
      !window.location.host.includes('localhost') && 
      !window.location.host.includes('127.0.0.1') &&
      cookieConsent?.preferences?.analytics // Only initialize if analytics cookies are accepted
    ) {
      posthog.init(posthogApiKey, {
        api_host: posthogHost,
        capture_pageview: false, // We'll handle this manually
        autocapture: true,
        persistence: 'localStorage',
        bootstrap: {
          distinctID: undefined, // Replace with user ID if available
          isIdentifiedID: false,
        },
        loaded: (posthog) => {
          if (import.meta.env.DEV) posthog.debug();
        },
      });
    }
  }, [cookieConsent?.preferences?.analytics]); // Re-run when analytics preference changes

  useEffect(() => {
    // Only capture pageview if PostHog is initialized and analytics are enabled
    if (posthog.__loaded && cookieConsent?.preferences?.analytics) {
      const properties = {
        $current_url: window.location.href,
        $pathname: location.pathname,
        $search: location.search,
        $hash: location.hash,
      };

      // Pass both event name and properties to capture
      posthog.capture('$pageview', properties, {
        send_instantly: true
      });
    }
  }, [location, cookieConsent?.preferences?.analytics]);

  return <>{children}</>;
};

// Export utility functions for opt-in/opt-out that respect cookie preferences
export const optOutOfTracking = () => {
  posthog.opt_out_capturing();
};

export const optInToTracking = () => {
  posthog.opt_in_capturing();
};

export const hasOptedOutOfTracking = () => {
  return posthog.has_opted_out_capturing();
};

export default PostHogProvider;
